// @flow
import React, { Suspense } from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import isVideo from 'shared/lib/isVideo';
import withAnalytics from 'shared/hoc/withAnalytics';
import styles from './TheatreCarousel.styles';
import ThumbnailRow from './ThumbnailRow';
import SocialIcon from './SocialIcon';
import Like from './Like';
import Icon from '@ff/ui/Icon';

const Slider = React.lazy(() => import(/* webpackChunkName: "react-slick" */ 'react-slick'));

if (!IS_NODE) {
  import(/* webpackChunkName: "slick-carousel" */ 'slick-carousel/slick/slick.css');
  import(/* webpackChunkName: "slick-carousel" */ 'slick-carousel/slick/slick-theme.css');
}

type Image = {
  url: string,
  size: string
};

type Props = {
  classes: Object,
  images: Array<Image>,
  startAt: Number,
  onClose: Function,
  thumbnails: Boolean,
  socialShare: Object,
  showSocialMediaIcons: Boolean,
  galleryId: String,
  userId: String,
  imagesInfo: Array<Object>,
  handleLikeClick: Function,
  fetchDomain: String,
};

class TheatreCarousel extends React.Component {
  props: Props = this.props;

  containerRef = React.createRef();

  constructor(props) {
    super(props);
    const { startAt, images } = this.props;
    const playedVideoList = [];

    if (isVideo(images[startAt].url)) {
      playedVideoList.push(startAt);
    }

    this.state = {
      playedVideoList,
      expandedSocialIcons: false,
      currentSlide: startAt,
      nextSlide: startAt
    };

    // Refs to all video elements
    this.videoRefs = {};
  }

  slider = React.createRef();

  componentDidMount = () => {
    document.addEventListener('keydown', this.handleKeyDown);
  };

  componentWillUnmount = () => {
    document.removeEventListener('keydown', this.handleKeyDown);
  };

  handleKeyDown = (e) => {
    const { onClose } = this.props;

    // ESC pressed
    if (e.keyCode === 27) {
      onClose();
    }
    // Right arrow key pressed
    if (e.keyCode === 39) {
      this.handleControl(1);
    }
    // Left arrow key pressed
    if (e.keyCode === 37) {
      this.handleControl(-1);
    }
  };

  getImageName = (url) => {
    const pathParts = url.split('/');

    return pathParts.pop();
  };

  handleControl = (direction) => {
    if (direction === 1) {
      this.slider.current.slickNext();
    } else if (direction === -1) {
      this.slider.current.slickPrev();
    }
  };

  playVideo = (index, url) => {
    this.videoRefs[index].play();
    this.setState(prevState => ({ playedVideoList: [...prevState.playedVideoList, index] }));

    this.props.analytics.trackUIEvent('Play gallery video', `${url}`);
  }

  setVideoRef = (element, index) => {
    // When 'infinite' == true in react-slick, it clones the
    // first and last components, which causes problems with video refs.
    // Ignore the cloned component.
    if (element && element.parentNode && element.parentNode.parentNode &&
      element.parentNode.parentNode.classList.contains('slick-cloned')) {
      return;
    }

    this.videoRefs[index] = element;
  }

  render() {
    const {
      classes: c, images, onClose, startAt, socialShare, showSocialMediaIcons, galleryId, userId, imagesInfo, handleLikeClick, fetchDomain
    } = this.props;
    const { expandedSocialIcons, currentSlide, nextSlide } = this.state;

    const slideIndex = (currentSlide >= 0 && currentSlide < images.length) ?
      currentSlide : 0;
    const controls = (
      <div className={c.controlsContainer}>
        {showSocialMediaIcons && socialShare &&
          <ShareIcons
            socialShare={socialShare}
            customUrl={images[slideIndex].socialUrl}
            expanded={expandedSocialIcons}
            classes={c}
            open={() => this.setState({ expandedSocialIcons: true })}
            close={() => this.setState({ expandedSocialIcons: false })}
          />
        }
        <Icon
          name="ff-icon-cross"
          className={classNames(c.closeBtn)}
          onClick={onClose}
        />
        {
          slideIndex < (images.length - 1) &&
          <i
            className={classNames(c.controlRight, c.control, 'material-icons')}
            onClick={() => this.handleControl(1)}
          >
            navigate_next

          </i>
        }
        {
          slideIndex > 0 &&
          <i
            className={classNames(c.controlLeft, c.control, 'material-icons')}
            onClick={() => this.handleControl(-1)}
          >
            navigate_before

          </i>
        }
      </div>
    );
    const settings = {
      arrows: false,
      dots: false,
      infinite: false,
      speed: 500,
      easing: 'ease-in-out',
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: startAt,
      className: c.gallerySlider,
      beforeChange: (oldIndex, newIndex) => {
        const { playedVideoList } = this.state;

        // Try to pause the video
        if (playedVideoList.includes(oldIndex)) {
          this.videoRefs[oldIndex].pause();
        }

        this.setState({ nextSlide: newIndex });
      },
      afterChange: (newIndex) => {
        this.setState({ currentSlide: newIndex });
      }
    };
    const { playedVideoList } = this.state;

    return (
      <div ref={this.containerRef} className={c.container}>
        {controls}
        <div className={c.galleryContainer}>
          <Suspense fallback={<div />}>
            <Slider ref={this.slider} {...settings}>
              {images.map((image, i) => {
                const imageName = this.getImageName(image.url);
                const shouldAutoPlay = !!(i === startAt);
                const videoWasPlayed = playedVideoList.includes(i);

                // undefined if there are 0 likes
                let likeData;

                if (imagesInfo) {
                  likeData = imagesInfo.find(imageInfo => imageInfo.imageId === image.url);
                }

                return (
                  <div
                    key={imageName}
                    className={c.galleryImageContainer}
                  >
                    <Like
                      galleryId={galleryId}
                      imageId={image.url}
                      likeData={likeData}
                      handleLikeClick={handleLikeClick}
                      userId={userId}
                      fetchDomain={fetchDomain}
                    />
                    {
                      isVideo(image.url) ? (
                        <div className={classNames(c.galleryImage, c.galleryVideoContainer)}>
                          <video
                            className={c.galleryImage}
                            controls={videoWasPlayed}
                            autoPlay={shouldAutoPlay}
                            disablePictureInPicture
                            controlsList="nodownload noremoteplayback"
                            ref={(e) => { this.setVideoRef(e, i); }}
                            src={image.url}
                            poster={image.coverUrl}
                          />
                          {
                            !videoWasPlayed &&
                            <div
                              className={c.playBtn}
                              onClick={() => this.playVideo(i, image.url)}
                            >
                              <i className="material-icons">play_arrow</i>
                            </div>
                          }
                        </div>
                      ) : (
                        <img
                          alt={imageName}
                          src={image.url}
                          className={c.galleryImage}
                        />
                      )
                    }
                  </div>
                );
              })}
            </Slider>
          </Suspense>
        </div>
        <ThumbnailRow
          images={images}
          classes={c}
          sliderGoTo={(i) => { this.slider.current.slickGoTo(i); }}
          currentSlide={nextSlide !== currentSlide ? nextSlide : currentSlide}
        />
      </div>
    );
  }
}

type SocialMediaIcon = {
  icon: string,
  url: string
};

type ShareIconsProps = {
  classes: Object,
  expanded: Boolean,
  open: Function,
  close: Function,
  customUrl: string,
  socialShare: {
    social_media_icons: Array<SocialMediaIcon>,
    qr_code_url: string
  }
};

const ShareIcons = (props: ShareIconsProps) => {
  const {
    classes: c,
    expanded,
    open,
    close,
    customUrl,
    socialShare: {
      social_media_icons: socialMediaIcons,
      qr_code_url: qrCodeUrl
    }
  } = props;

  return socialMediaIcons.length > 0 && (
    <div
      className={c.socialShare}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      <div
        className={classNames(c.iconList, expanded && c.expanded)}
      >
        {socialMediaIcons.map((link, index) => {
          const qrCode = customUrl || qrCodeUrl;
          const linkUrl = customUrl ? replaceUrl(link.url, customUrl) : link.url;

          return (
            <SocialIcon
              customClass={c.socialIcon}
              key={index}
              link={{ ...link, url: linkUrl }}
              generateQRCode={`${qrCode}`}
              showQRCodeAbove={false}
            />
          );
        })}
      </div>
      <Icon
        name="ff-icon-share"
        className={classNames(c.shareIcon)}
      />
    </div>
  );
};

/**
* Replace the default URL with the override
* Videos have unique share URLs, which should replace the default URL used in the widget.
*/
const replaceUrl = (originalLink, override) => {
  const parts = originalLink.split('?');

  if (parts.length === 1) {
    // wechat is just a url
    return override;
  }
  const params = parts[1].split('&');
  const res = params.map((p) => {
    const kv = p.split('=');

    // url => linkedin, weibo; u => facebook; body => email; text => twitter
    if (kv[0] === 'url' || kv[0] === 'u' || kv[0] === 'body' || kv[0] === 'text') {
      kv[1] = override;
    }
    return `${kv[0]}=${kv[1]}`;
  });

  return `${parts[0]}?${res.join('&')}`;
};

export default withAnalytics(injectSheet(styles)(TheatreCarousel));
